import { useEffect, useMemo, useState } from "react";
import { DataService } from "@/services/data";
import { FunctionDescription, VariableOption } from "@/types/formula";
import { useFormulaScope, withScopeVariables } from "@/providers/formula-scope";

/**
 * The two referentials a formula input reads: the variables it suggests and the functions
 * it offers.
 *
 * Fetched once per url for the whole session and shared by every field, which is the only
 * reason the catalogue may be four hundred entries long: a node form holds a dozen formula
 * fields and they all point at the same `catalog_url`, so a fetch per field would be a
 * dozen requests for one list that changes on deploy.
 *
 * The cache holds the promise rather than the result, so twelve fields mounting in the same
 * render make one request instead of racing to be first.
 */
const CACHE = new Map<string, Promise<unknown[]>>();

function load(url: string): Promise<unknown[]> {
    const cached = CACHE.get(url);
    if (cached) return cached;
    const pending = DataService.client
        .url(url)
        .get()
        .json<unknown>()
        .then(data => (Array.isArray(data) ? data : ((data as { results?: unknown[] })?.results ?? [])))
        .catch(() => {
            // A referential that cannot be reached must not be remembered as empty: the
            // next field to mount asks again, and the input stays usable meanwhile.
            CACHE.delete(url);
            return [];
        });
    CACHE.set(url, pending);
    return pending;
}

/** Forget every cached referential. For tests, which each serve their own catalogue. */
export function resetFormulaCatalogCache() {
    CACHE.clear();
}

type Referential<T> = { options: T[]; loading: boolean };

function useReferential<T>(url: string | null | undefined): Referential<T> {
    const [options, setOptions] = useState<T[]>([]);
    const [loading, setLoading] = useState(Boolean(url));

    useEffect(() => {
        if (!url) {
            setOptions([]);
            setLoading(false);
            return;
        }
        let alive = true;
        setLoading(true);
        load(url).then(data => {
            if (!alive) return;
            setOptions(data as T[]);
            setLoading(false);
        });
        return () => {
            alive = false;
        };
    }, [url]);

    return { options, loading };
}

/**
 * Every field a formula may read, in the order the server put them, then the variables
 * the document being edited declares (`FormulaScopeProvider`).
 *
 * The order is load-bearing and never re-sorted here: the canonical names come first
 * because they are the ones an author should reach for, and the groups are headings. The
 * document's rows are appended rather than cached with the referential, so a variable
 * declared in the drawer is suggested as soon as the draft answers, in every field.
 */
export function useVariableCatalog(catalogUrl: string | null | undefined): Referential<VariableOption> {
    const fetched = useReferential<VariableOption>(catalogUrl);
    const { variables } = useFormulaScope();
    const options = useMemo(() => (catalogUrl ? withScopeVariables(fetched.options, variables) : fetched.options), [catalogUrl, fetched.options, variables]);
    return { options, loading: fetched.loading };
}

/**
 * The closed vocabulary, with the signature and the summary the engine wrote for each.
 *
 * Read from the server rather than listed here on purpose: the vocabulary is a dictionary
 * in `formula.functions`, a host may register its own, and a palette holding a second copy
 * of that list would be wrong the first time one is added. An empty answer is therefore
 * reported as such by the palette instead of being replaced by a hand-written list.
 */
export function useFormulaFunctions(url: string): Referential<FunctionDescription> {
    return useReferential<FunctionDescription>(url);
}
